import React, { useEffect, useState } from "react";
import { adminApi } from "../../services/adminApi";

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [form, setForm] = useState({ subject: "", body: "", recipientType: "all", recipientIds: [] });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([adminApi.listMessages(), adminApi.listDrivers()]);
      setMessages(m.messages);
      setDrivers(d.drivers);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function handleSelectMessage(msg) {
    setSelected(msg);
    try {
      const t = await adminApi.getMessageThread(msg._id);
      setThread(t);
    } catch (err) { console.error(err); }
  }

  async function handleSend(e) {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await adminApi.sendMessage(form);
      setShowCompose(false);
      setForm({ subject: "", body: "", recipientType: "all", recipientIds: [] });
      load();
    } catch (err) { setError(err.message); }
    finally { setSending(false); }
  }

  function toggleDriver(id) {
    setForm((f) => ({
      ...f,
      recipientIds: f.recipientIds.includes(id) ? f.recipientIds.filter((r) => r !== id) : [...f.recipientIds, id],
    }));
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
        <button onClick={() => setShowCompose(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Compose
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Message list */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? <div className="p-8 text-center text-gray-400">Loading…</div> :
            messages.length === 0 ? <div className="p-8 text-center text-gray-400">No messages yet.</div> : (
              <ul className="divide-y divide-gray-100">
                {messages.map((m) => (
                  <li key={m._id}>
                    <button onClick={() => handleSelectMessage(m)}
                      className={`w-full text-left px-5 py-4 hover:bg-gray-50 transition ${selected?._id === m._id ? "bg-blue-50" : ""}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">{m.subject}</p>
                        <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${m.isGlobal ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                          {m.isGlobal ? "All Drivers" : "Individual"}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{m.senderName} · {new Date(m.createdAt).toLocaleDateString()}</p>
                      <p className="text-sm text-gray-600 mt-1 truncate">{m.body}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
        </div>

        {/* Thread view */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          {!selected ? (
            <div className="h-48 flex items-center justify-center text-gray-400">
              <p>Select a message to view the thread</p>
            </div>
          ) : (
            <div>
              <h2 className="font-bold text-gray-900 mb-1">{selected.subject}</h2>
              <p className="text-xs text-gray-500 mb-4">
                {selected.senderName} · {new Date(selected.createdAt).toLocaleString()}
                {selected.isGlobal && <span className="ml-2 bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs">Sent to all drivers</span>}
              </p>
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap mb-4">{selected.body}</div>
              {thread?.replies?.map((r) => (
                <div key={r._id} className={`mt-3 rounded-lg p-4 text-sm ${r.senderType === "driver" ? "bg-blue-50 text-blue-900" : "bg-gray-50 text-gray-700"}`}>
                  <p className="font-medium text-xs mb-1">{r.senderName} ({r.senderType}) · {new Date(r.createdAt).toLocaleString()}</p>
                  <p className="whitespace-pre-wrap">{r.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Compose modal */}
      {showCompose && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-screen overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Compose Message</h2>
            {error && <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-2 mb-3 text-sm">{error}</div>}
            <form onSubmit={handleSend} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-600">Send to</label>
                <select value={form.recipientType} onChange={(e) => setForm({ ...form, recipientType: e.target.value, recipientIds: [] })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="all">All Drivers</option>
                  <option value="individual">Specific Drivers</option>
                </select>
              </div>
              {form.recipientType === "individual" && (
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-2">Select Drivers</label>
                  <div className="border border-gray-200 rounded-lg overflow-auto max-h-40">
                    {drivers.map((d) => (
                      <label key={d._id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={form.recipientIds.includes(d._id)} onChange={() => toggleDriver(d._id)} className="rounded" />
                        <span className="text-sm">{d.firstName} {d.lastName}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-600">Subject</label>
                <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Message</label>
                <textarea required rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={sending}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60 transition">
                  {sending ? "Sending…" : "Send Message"}
                </button>
                <button type="button" onClick={() => setShowCompose(false)}
                  className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
